import { v4 as uuidv4 } from 'uuid'
import { PositionedArrayManager } from '@opallabs/lambda'
import { defaultItemSorter, defaultPositionKey } from './defaults'

export const arrayManager = new PositionedArrayManager()

export const getResponseId = (response) => response.payload.data.id

export const identifierFor = (item, map = {}) =>
  item && (item.uuid || map[item.id] || item.id)

/**
 * Returns whether the specified item ID is attached to a pending 'createItem' request.
 * If so, we will need to enqueue any further requests, so they do not get processed
 * before the creation request completes.
 */
export const beingCreated = (itemId, { updates }) =>
  updates.find(({ creator, identifier }) => creator && itemId === identifier)

const updatesForItem = (item, { updates = [], idMap }) =>
  updates.filter(({ identifier }) => identifier === identifierFor(item, idMap))

const errorsForItem = (item, { errors = [], idMap }) =>
  errors.filter(({ identifier }) => identifier === identifierFor(item, idMap))

const decoratorProps = (idx, { items }) => ({
  idx,
  isFirst: idx === 0,
  isLast: idx === items.length - 1,
})

export const propsFor = (item, idx, state) => ({
  updates: updatesForItem(item, state),
  errors: errorsForItem(item, state),
  ...decoratorProps(idx, state),
})

const placeholderAt = (insertAt, items, { positionKey }) => {
  const newPosition = arrayManager.insertAt(items, insertAt)[insertAt]
  const newItem = {
    autoGenerated: true,
    isPlaceholder: true,
    uuid: uuidv4(),
    [positionKey]:
      newPosition === 0 ? 0 : newPosition || Number.MAX_SAFE_INTEGER,
  }
  return newItem
}

export const newItemReducer =
  (
    insertAt,
    { itemSorter = defaultItemSorter, positionKey = defaultPositionKey }
  ) =>
  ({ items = [], updates = [] }) => {
    const update = placeholderAt(insertAt, items, { positionKey })
    return {
      items: items.concat(update).sort(itemSorter(positionKey)),
      updates: updates.concat({ identifier: update.uuid, update }),
    }
  }

export const placeholderTypes = {
  none: () => false,
  whenEmpty: (items = [], props = {}) =>
    !items.length && {
      ...placeholderAt(0, items, props),
      emptyListPlaceholder: true,
    },
  bottom: (items = [], props = {}) =>
    (!items.length ||
      (items.length && Boolean(!items.find((item) => item.isPlaceholder)))) &&
    placeholderAt(items.length, items, props),
}

export const placeholderOptions = Object.keys(placeholderTypes)

export const enqueueDelayedUpdate =
  (item, update) =>
  ({ items, updateQueue }) => {
    const newItem = { ...item, ...update }
    const itemIndex = items.findIndex(
      (anItem) => identifierFor(anItem) === identifierFor(item)
    )
    const newItems = [...items]
    newItems[itemIndex] = newItem

    return {
      items: newItems,
      updateQueue: [...(updateQueue || []), [item, update]],
    }
  }

export const addMap = (items) =>
  items.map(({ canAddAfter, canAddBefore }, idx) => [
    (canAddBefore === true &&
      (idx === 0 || items[idx - 1].canAddAfter !== false)) ||
      (canAddBefore !== false &&
        (idx === 0 || items[idx - 1].canAddAfter !== false)),
    (canAddAfter === true &&
      (idx === items.length - 1 || items[idx + 1].canAddBefore !== false)) ||
      (canAddAfter !== false &&
        (idx === items.length - 1 || items[idx + 1].canAddBefore !== false)),
  ])
